import { useQueries } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'query-string';
import { BUOY_GPT_API_V1 } from 'src/interviews/requests/settings';
import { dedupe } from 'src/utils/array';
import { removeQueryString } from 'src/utils/url';

import type { UseQueryResult } from '@tanstack/react-query';
import type { IDiagnosis, IInterviewResults, ITopProduct } from 'src/packages/polaris/types';
import type { RelatedReadingType } from '../../components/RelatedReading/RelatedReading';
import type { RichEndingsProps } from '../../components/RichEndings';

type RequestOptions = {
  key: string[];
  params: {
    count: number;
    page_types: string[];
    query: string;
  };
}[];

type Response = {
  query: string;
  related_readings: RelatedReadingType[];
};

function useRelatedReading(props: RichEndingsProps) {
  const requests = Requests.create(props);

  const queries = requests.map((request) => ({
    queryKey: request.key,
    queryFn: async () => {
      const response = await axios.get(`${BUOY_GPT_API_V1}/search/related_readings`, {
        params: request.params,
        paramsSerializer: (params) => qs.stringify(params),
      });

      return response.data;
    },
    staleTime: Infinity,
  }));

  const responses = useQueries({
    queries,
  });

  return Requests.deserialize(responses);
}

class Requests {
  static DEFAULT_PARAMS = {
    count: 5,
    page_types: ['article', 'blogPost'],
  };

  static create(options: RichEndingsProps): RequestOptions {
    const { interviewType, query, products, result } = options;

    switch (interviewType) {
      case 'dxoc':
        return this.#dxocRequest(result);
      case 'emergency':
        return this.#emergencyRequest(result);
      case 'symptoms':
        return this.#symptomsRequest(result);
      case 'treatment':
        return this.#treatmentRequest(query);
      case 'treatmentGuidance':
        return this.#treatmentGuidanceRequest(products);
      case 'txoc':
        return this.#txocRequest(query);
      default:
        throw new Error(`Related reading for interview type: ${interviewType} is not implemented.`);
    }
  }

  static deserialize(responses: UseQueryResult<Response>[]) {
    const allReadings = responses.flatMap((res) => res.data?.related_readings || []);

    const uniqueReadings = dedupe<RelatedReadingType>(
      allReadings,
      (r: RelatedReadingType) => r.url,
    );

    return {
      data: {
        related_readings: uniqueReadings,
      },
      isError: responses.some((res) => res.isError),
      isLoading: responses.some((res) => res.isLoading),
      isSuccess: responses.every((res) => res.isSuccess),
    };
  }

  static #dxocRequest(result?: IInterviewResults) {
    const diagnosis = result?.diagnosis_of_concern;

    if (!diagnosis?.name) {
      return [];
    }

    return [
      {
        key: [`dxoc-relatedReading-${diagnosis.name}`],
        params: {
          ...this.DEFAULT_PARAMS,
          query: diagnosis.name,
        },
      },
    ];
  }

  static #emergencyRequest(result?: IInterviewResults) {
    const differential = result?.differential || ([] as IDiagnosis[]);

    if (!differential?.length) {
      if (result?.triage_explanation?.explanation) {
        return [
          {
            key: [`emergency-relatedReading`],
            params: {
              ...this.DEFAULT_PARAMS,
              count: 3,
              query: result?.triage_explanation?.explanation,
            },
          },
        ];
      }
      return [];
    }

    return differential.map((d) => ({
      key: [`emergency-relatedReading-${d.name}`],
      params: {
        ...this.DEFAULT_PARAMS,
        count: 3,
        query: d.name,
      },
    }));
  }

  static #symptomsRequest(result?: IInterviewResults) {
    const differential = result?.differential || ([] as IDiagnosis[]);

    if (!differential?.length) {
      return [];
    }

    return differential.map((d) => ({
      key: [`symptoms-relatedReading-${d.name}`],
      params: {
        ...this.DEFAULT_PARAMS,
        count: 3,
        query: d.name,
      },
    }));
  }

  static #treatmentRequest(query?: string) {
    if (!query) {
      return [];
    }

    return [
      {
        key: [`treatment-relatedReading-${query}`],
        params: {
          ...this.DEFAULT_PARAMS,
          query,
        },
      },
    ];
  }

  static #treatmentGuidanceRequest(products?: ITopProduct[]) {
    if (!products?.length) {
      return [];
    }

    return products.map((p) => ({
      key: [`treatmentGuidance-relatedReading-${p.affiliate_url}`],
      params: {
        ...this.DEFAULT_PARAMS,
        count: 3,
        query: removeQueryString(p.affiliate_url),
      },
    }));
  }

  static #txocRequest(query?: string) {
    if (!query) {
      return [];
    }

    return [
      {
        key: [`txoc-relatedReading-${query}`],
        params: {
          ...this.DEFAULT_PARAMS,
          query,
        },
      },
    ];
  }
}

export { Requests };
export default useRelatedReading;
